import { z } from 'zod/v3';
import { ATCStackFrameStatusSchema } from '../atc';

const jsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ])
);

const messageSchema = z.object({
  id: z.string().uuid(),
  role: z.string(),
  position: z.number(),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  usage: jsonValueSchema.nullable(),
  message: jsonValueSchema,
});

export type StoredMessage = z.infer<typeof messageSchema>;

const chatStatusEventSchema = z.object({
  id: z.string().uuid(),
  eventType: z.string(),
  createdAt: z.string(),
  metadataId: z.string().uuid().nullable(),
});

export type ChatStatusFrameOutput = {
  id: string;
  parentId: string | null;
  createdAt: string;
  fpType: string;
  displayName: string;
  depth: number;
  position: number;
  status: z.infer<typeof ATCStackFrameStatusSchema>;
  /**
   * Credit cost for this frame. May be an estimate derived from token counts
   * and model multipliers when official metered_credits have not yet been
   * written by the billing pipeline, or for historical frames created before
   * credit metering was introduced (before March 2026). Do not use for invoicing.
   */
  credits: number;
  events: z.infer<typeof chatStatusEventSchema>[];
  children: ChatStatusFrameOutput[];
  /** Human-readable error message when frame has failed (from last error event) */
  lastError?: string;
};

const chatStatusFrameSchema: z.ZodType<ChatStatusFrameOutput> = z.lazy(
  () =>
    z.object({
      id: z.string().uuid(),
      parentId: z.string().uuid().nullable(),
      createdAt: z.string(),
      fpType: z.string(),
      displayName: z.string(),
      depth: z.number(),
      position: z.number(),
      status: ATCStackFrameStatusSchema,
      credits: z.number(),
      events: z.array(chatStatusEventSchema),
      children: z.array(chatStatusFrameSchema),
      lastError: z.string().optional(),
    }) as z.ZodType<ChatStatusFrameOutput>
);

/**
 * Lifetime cost totals for a conversation. `credits` sums every
 * `atc_resource_usage` row across every run tied to the chat (and nested
 * sub-agent chats). `hasActiveFrame` is true while any stack frame in the
 * current run tree has not reached a terminal state (success / fail / cancel).
 */
const chatCostTotalsSchema = z.object({
  credits: z.number(),
  hasActiveFrame: z.boolean(),
  breakdown: z.object({
    llm: z.number(),
    toolcall: z.number(),
  }),
});

export type ChatCostTotals = z.infer<typeof chatCostTotalsSchema>;
